import { Injectable } from '@angular/core';

import { EMPTY, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { MdbModalRef, MdbModalService } from 'src/lib/mdb5-adapted/public-api';

import { BankAccListComponent } from '../bank-acc-list/bank-acc-list.component';
import { ClientShowModel } from '../core/models/client-show.model';
import { PaySlipModel1 } from '../core/models/pay-slip.model';
import { QuoteModel } from '../core/models/quote.model';
import { EmploymentDetailsComponent } from '../employment-details/employment-details.component';
import { UpdateEmployerComponent } from '../features/branch-employers/update-employer/update-employer.component';
import { DocumentScanningComponent } from '../features/document-scanning/document-scanning.component';
import { AddressesComponent } from '../modules/addresses/addresses.component';
import { AuditTrailComponent } from '../modules/audit-trail/audit-trail.component';
import { BankingDetailsComponent } from '../modules/banking-details/banking-details.component';
import { BiometricsDialogComponent } from '../modules/biometrics-dialog/biometrics-dialog.component';
import {
  BranchManagerOverrideComponent,
} from '../modules/biometrics-dialog/branch-manager-override/branch-manager-override.component';
import { ClientDocumentsComponent } from '../modules/client-documents/client-documents.component';
import { FingerprintSigninComponent } from '../modules/fingerprint-signin/fingerprint-signin.component';
import { LoanChecklistDialogComponent } from '../modules/loan-checklist-dialog/loan-checklist-dialog.component';
import { NextOfKinComponent } from '../modules/next-of-kin/next-of-kin.component';
import { NotesComponent } from '../modules/notes/notes.component';
import { PayslipDialogComponent } from '../modules/payslip-dialog/payslip-dialog.component';
import { RejectOfferComponent, RejectType } from '../modules/reject-offer/reject-offer.component';
import { LexisNexisReportComponent } from '../modules/reports/lexis-nexis-report/lexis-nexis-report.component';
import { OfferValuesComponent } from '../offer-values/offer-values.component';
import { CbsGuideComponent } from '../register-loan/cbs-guide/cbs-guide.component';
import { RiskMatrixScoreComponent } from '../risk-matrix-score/risk-matrix-score.component';
import {
  AgentComplianceNotificationDialogComponent,
} from './dialogs/agent-compliance-notification-dialog/agent-compliance-notification-dialog.component';
import { BranchLoansReportComponent } from './dialogs/branch-loans-report/branch-loans-report.component';
import {
  BranchPerformanceReportComponent,
} from './dialogs/branch-performance-report/branch-performance-report.component';
import { BranchReportByAreaComponent } from './dialogs/branch-report-by-area/branch-report-by-area.component';
import { BranchReportComponent } from './dialogs/branch-report/branch-report.component';
import { BranchStatsReportComponent } from './dialogs/branch-stats-report/branch-stats-report.component';
import { ClientChangeLogReportComponent } from './dialogs/client-change-log-report/client-change-log-report.component';
import { ClientMismatchDialogComponent } from './dialogs/client-mismatch-dialog/client-mismatch-dialog.component';
import { CompletedLoansReportComponent } from './dialogs/completed-loans-report/completed-loans-report.component';
import { ConfirmDialogComponent } from './dialogs/confirm-dialog/confirm-dialog.component';
import { FieldLoansReportComponent } from './dialogs/field-loans-report/field-loans-report.component';
import { LmsDataReportComponent } from './dialogs/lms-data-report/lms-data-report.component';
import { LMSDialogComponent } from './dialogs/lms-dialog/lms-dialog.component';
import { LtApprovalDialogComponent } from './dialogs/lt-approval-dialog/lt-approval-dialog.component';
import { MaximumLoansDialogComponent } from './dialogs/maximum-loans-dialog/maximum-loans-dialog.component';
import { NupayReportComponent } from './dialogs/nupay-report/nupay-report.component';
import { OverrideReportComponent } from './dialogs/override-report/override-report.component';
import {
  ScanningMonitoringReportComponent,
} from './dialogs/scanning-monitoring-report/scanning-monitoring-report.component';
import { UserChangeLogReportComponent } from './dialogs/user-change-log-report/user-change-log-report.component';
import { DocumentViewerComponent } from './ui/document-viewer/document-viewer.component';

/**
 * Test-safe stub implementation of LMSDialogHandlerService.
 *
 * Purpose:
 * - Break circular imports during test bootstrap by avoiding application component imports.
 * - Provide no-op implementations for the methods used throughout the application so
 *   TypeScript compilation succeeds and tests can inject the service safely.
 *
 * Note: This is intentionally minimal and should be replaced by the real implementation
 * for production builds or during feature development. For long-term correctness,
 * resolve the original circular import and restore the full service implementation.
 */
@Injectable({ providedIn: 'root' })
export class LMSDialogHandlerService {

  constructor(public modalService: MdbModalService) {}

  openClientDocuments(
    clientModel: ClientShowModel,
    quoteId?: number | string,
    readonly = false,
  ) {
    this.modalService.open(ClientDocumentsComponent, {
      modalClass: 'modal-xl modal-top client-documents-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        quoteId: quoteId || clientModel.id,
        clientShow: clientModel,
        isReadOnly: readonly,
      },
    });
  }

  openEmploymentDialog(
    employment: any,
    quoteModel: ClientShowModel,
    addresses: any,
    readonly = false,
  ) {
    return this.modalService.open(EmploymentDetailsComponent, {
      modalClass: 'modal-xl modal-top employment-details-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        employment,
        addresses,
        quoteEmployName: employment.name,
        clientShowModel: quoteModel,
        isReadOnly: readonly,
      },
    });
  }

  openUpdateEmployerDialog(employerModel: any) {
    return this.modalService.open(UpdateEmployerComponent, {
      modalClass: 'modal-lg modal-top update-employer-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        employerModel,
      },
    });
  }

  openAddressDialog(quoteModel: QuoteModel, addresses: any, readonly = false) {
    return this.modalService.open(AddressesComponent, {
      modalClass: 'modal-lg modal-top address-dialog-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        addresses,
        clientDetails: quoteModel,
        isReadOnly: readonly,
      },
    });
  }

  openNextofKinDialog(
    quoteModel: QuoteModel,
    nextOfKinData: any[],
    readonly = false,
  ) {
    return this.modalService.open(NextOfKinComponent, {
      modalClass: 'modal-lg modal-top next-of-kin-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        nextOfKin: nextOfKinData[0],
        clientDetails: quoteModel,
        addresses: nextOfKinData[1],
        isReadOnly: readonly,
      },
    });
  }

  openBankingDetailsDialog(
    clientDetails: QuoteModel,
    bankingDetails: any,
    readonly = false,
  ) {
    return this.modalService.open(BankingDetailsComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      modalClass: 'modal-lg modal-top banking-details-modal',
      data: {
        bankingDetails,
        clientDetails,
        isReadOnly: readonly,
      },
    });
  }

  openDocScanning(
    quoteModel: QuoteModel,
    quoteId?: number | string,
    readonly = false,
    fromLoanDetails = false,
  ) {
    return this.modalService.open(DocumentScanningComponent, {
      modalClass: 'modal-xl modal-top client-documents-modal',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        quoteId: `${quoteId}`,
        clientDetails: quoteModel,
        isReadOnly: readonly,
        fromLoanDetails: fromLoanDetails,
      },
    });
  }

  openDocumentViewer(fileSrc: string | Blob | Uint8Array, pfileName: string) {
    return this.modalService.open(DocumentViewerComponent, {
      modalClass: 'modal-xl modal-dialog-centered',
      backdrop: true,
      ignoreBackdropClick: true,
      // modalClass: 'modal-xl modal-dialog modal-dialog-centered',
      // containerClass: 'd-flex d-flex-xl',
      // backdrop: true,
      // ignoreBackdropClick: true,
      // keyboard: true,
      data: {
        fileSrc,
        pdffileName: pfileName,
      },
    });
  }

  openCompuscanOffersDialog(
    compuscanOffers: any,
    withOverride: boolean = false,
  ) {
    return this.modalService.open(OfferValuesComponent, {
      modalClass: 'modal-lg modal-top',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        compuscanOffers,
        withOverride,
      },
    });
  }

  openLexisNexisDialog(header: any, message: any, title: string) {
    this.modalService.open(ConfirmDialogComponent, {
      modalClass: 'modal-md modal-top',
      data: {
        header,
        message,
        title,
      },
    });
  }

  openConfirmationDialog(header: any, message: any, title: string, showClose: boolean = false) {
    return this.modalService.open(ConfirmDialogComponent, {
      modalClass: 'modal-md modal-top',
      data: {
        header,
        message,
        title,
        showClose
      },
    });
  }

  openBiometricsDialog(
    biometricsInfo: any,
    biometricType: string,
    userBiometricsData?: any,
  ) {
    return this.modalService.open(BiometricsDialogComponent, {
      modalClass: 'modal-md modal-top',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        biometricsInfo: biometricsInfo,
        biometricType,
        userBiometricsData: userBiometricsData,
      },
    });
  }

  openBMOTPOverrideDialog(
    preauthBiometricsData: any,
    biometricType: string,
    userBiometricsData?: any,
  ) {
    return this.modalService?.open(BranchManagerOverrideComponent, {
      modalClass: 'modal-md modal-dialog-centered',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        biometricsInfo: preauthBiometricsData || userBiometricsData,
        biometricType,
      },
    });
  }

  openLMSDialog(
    confirmMsg: string,
    msgtitle: string,
    withTimer: boolean = false,
    isError = false,
  ) {
    const lmsDialogRef = this.modalService.open(LMSDialogComponent, {
      modalClass: 'modal-dialog-centered modal-info',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        title: msgtitle,
        message: !confirmMsg || confirmMsg == '' ? 'Please wait!' : confirmMsg,
        showTimer: withTimer,
        isErrorOccured: isError,
      },
    });
    return lmsDialogRef;
  }

  openMdbComplianceNotification() {
    const lmsDialogRef = this.modalService.open(
      AgentComplianceNotificationDialogComponent,
      {
        modalClass: 'modal-lg modal-dialog-centered',
        backdrop: true,
        ignoreBackdropClick: true,
        keyboard: true,
      },
    );
    return lmsDialogRef;
  }

  openSigninDialog() {
    const fpsignin = this.modalService.open(FingerprintSigninComponent, {
      ignoreBackdropClick: true,
      keyboard: false,
      modalClass: 'modal-md modal-top',
      data: {
        biometricType: 'user',
      },
    });
    return fpsignin;
  }

  openRiskMatrixScoresDialog(riskAnalysis: any) {
    this.modalService.open(RiskMatrixScoreComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      nonInvasive: false,
      modalClass: 'modal-md  modal-dialog-centered',
      data: {
        riskAnalysis,
      },
    });
  }

  openRejectOfferDialog(
    currQuote: any,
    setToCancelOrReject: RejectType = 'Rejection',
  ) {
    return this.modalService.open(RejectOfferComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      nonInvasive: false,
      animation: true,
      modalClass: 'modal-lg modal-dialog-centered',
      data: {
        rejectOfferInfo: currQuote,
        cancelOrReject: setToCancelOrReject,
      },
    });
  }

  /**
   * Centralized method to handle reject/cancel offer workflow
   * Opens reject dialog, processes user input, and calls backend service
   *
   * @param quoteData - Quote data with quoteId
   * @param rejectType - 'Rejection' or 'Cancellation'
   * @param quoteStoreService - Service to call rejectOffer API
   * @param globalUtility - Utility service for user messages and navigation
   * @param options - Optional configuration
   * @returns Observable that completes when workflow finishes
   */
  handleRejectOfferWorkflow(
    quoteData: any,
    rejectType: RejectType,
    quoteStoreService: any,
    globalUtility: any,
    options?: {
      confirmRejectionLetter?: boolean;
      printLetterFn?: () => void;
      customSuccessHandler?: (response: any) => void;
      customErrorHandler?: (error: any) => void;
      loadingMessage?: string;
      navigateOnSuccess?: boolean;
      navigationDelay?: number;
    },
  ): Observable<any> {
    const config = {
      confirmRejectionLetter: false,
      navigateOnSuccess: true,
      navigationDelay: 2000,
      loadingMessage:
        rejectType === 'Cancellation'
          ? 'Cancelling application...'
          : 'Rejecting offer...',
      ...options,
    };

    const currQuote = { ...quoteData };
    currQuote.quoteId = quoteData.id || quoteData.quoteId;

    return this.openRejectOfferDialog(currQuote, rejectType).onClose.pipe(
      switchMap((resultDlg) => {
        if (!resultDlg) {
          // User cancelled dialog
          return EMPTY;
        }

        // Prepare quote data with rejection reasons
        currQuote.rejectOfferReason = resultDlg.rejectOfferReason;
        currQuote.userName = globalUtility.getGlobalData('userDisplay');

        if (rejectType === 'Cancellation') {
          currQuote.cancelRequest = 'Cancel';
        }

        if (config.loadingMessage) {
          globalUtility.startLoading(config.loadingMessage);
        }

        // Call backend service
        return quoteStoreService.rejectOffer(currQuote).pipe(
          tap({
            next: (response: any) => {
              if (response?.errorMessage) {
                globalUtility.error(
                  response.errorMessage,
                  `${rejectType} Failed`,
                );
                return;
              }

              // Print rejection letter if requested
              if (resultDlg.requiresRejectionLetter ?? false) {
                globalUtility.startLoading('Preparing FMB Rejection Letter..');
                quoteStoreService
                  .printFMBRejectionLetter(currQuote.quoteId || 0)
                  .subscribe({
                    next: (response) => {
                      if (response) {
                        const file = new Blob([response], {
                          type: 'application/pdf',
                        });
                        this.openDocumentViewer(
                          URL.createObjectURL(file),
                          `${currQuote.quoteId}_FMBRejectionLetter.pdf`,
                        );
                      }
                    },
                    error: (error) => {
                      console.log('Error: ', error);
                      globalUtility.stopLoading();
                    },
                    complete: () => {
                      globalUtility.stopLoading();
                    },
                  });
              }

              if (config.confirmRejectionLetter && config.printLetterFn) {
                //config.printLetterFn();
              }

              // Handle custom success logic
              if (config.customSuccessHandler) {
                config.customSuccessHandler(response);
              }

              // Navigate after delay
              if (config.navigateOnSuccess) {
                setTimeout(() => {
                  globalUtility.navigateToLanding();
                }, config.navigationDelay);
              }
            },
            error: (err: any) => {
              if (config.customErrorHandler) {
                config.customErrorHandler(err);
              } else {
                console.error(`${rejectType} error:`, err);
                globalUtility.error(
                  `Error during ${rejectType.toLowerCase()}`,
                  rejectType,
                );
              }
              globalUtility.stopLoading();
            },
            complete: () => {
              if (config.loadingMessage) {
                globalUtility.stopLoading();
              }
            },
          }),
        );
      }),
    );
  }

  openPayslipDialog(month: string | number, payslipModel?: PaySlipModel1 | null, clientType?: string) {
    return this.modalService?.open(PayslipDialogComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      nonInvasive: false,
      modalClass: 'modal-md modal-dialog-centered',
      data: {
        month,
        payslipModel,
        clientType,
      },
    });
  }

  openAuditTrailDialogByQuoteId(quoteId: number) {
    return this.modalService.open(AuditTrailComponent, {
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      nonInvasive: false,
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      data: {
        quoteId,
      },
    });
  }

  openAuditTrailDialogbyIdNumber(idNumber: string) {
    return this.modalService.open(AuditTrailComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      //modalClass: 'modal modal-xl d-flex d-flex-xl',
      //containerClass: 'modal-dialog-scrollable',
      data: {
        idNumber,
      },
    });
  }

  openCBSGuideDialog() {
    this.modalService.open(CbsGuideComponent, {
      modalClass: 'modal-md modal-dialog-centered',
    });
  }

  openQuoteNotesDialog(quoteID: number, quotenotes: any, readonly = false) {
    return this.modalService.open(NotesComponent, {
      modalClass: 'modal-lg modal-dialog-centered',
      data: {
        quoteid: quoteID,
        existingNotes: quotenotes,
        readOnly: readonly,
      },
    });
  }

  openBankAccountListDialog(
    bankingDetails: any,
    clientDetails: QuoteModel,
    readonly = false,
  ) {
    return this.modalService.open(BankAccListComponent, {
      modalClass: 'modal-lg modal-dialog-centered',
      data: {
        bankingDetails,
        clientDetails,
        readOnly: readonly,
      },
    });
  }

  openClientChangeLogReportDailog(lstClientLogs: any) {
    return this.modalService.open(ClientChangeLogReportComponent, {
       modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      data: {
        clientChangeLogList: lstClientLogs,
        isReadOnly: true,
      },
    });
  }
  openUserChangeLogReportDailog(lstUserLogs: any) {
    return this.modalService.open(UserChangeLogReportComponent, {
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      data: {
        userChangeLogList: lstUserLogs,
        isReadOnly: true,
      },
    });
  }

  openOverrideReportDailog(lstOverrideLogs: any) {
    return this.modalService.open(OverrideReportComponent, {
       modalClass: 'modal-xl modal-dialog-centered',
      data: {
        overrideList: lstOverrideLogs,
        //branchName: branchName,
        isReadOnly: true,
      },
    });
  }

  openBranchLoansDailog(lstLoans: any) {
    return this.modalService.open(BranchLoansReportComponent, {
      modalClass: 'modal-xl modal-dialog-centered',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        loansList: lstLoans,
        isReadOnly: true,
      },
    });
  }

  openLTApprovalDialog(data: { clientType: 'salaried' | 'pensioner'; loanAmount: number; quoteData: QuoteModel }): Observable<any> {
    const modalRef: MdbModalRef<LtApprovalDialogComponent> = this.modalService.open(LtApprovalDialogComponent, {
      modalClass: 'modal-md modal-top',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      data: data,
    });

    return modalRef.onClose.pipe(
      switchMap((result) => {
        if (result) {
          return new Observable((observer) => {
            observer.next(result);
            observer.complete();
          });
        }
        return EMPTY;
      })
    );
  }


  //TODO ----------------------------------------------------
  openBranchPerformanceDialog(
    lstPerformanceResults: any[],
    totalBranchCapital: any,
    totalBranchCount: any,
    startDate: any,
    endDate: any,
  ) {
    return this.modalService.open(BranchPerformanceReportComponent, {
      modalClass: 'modal-xl modal-dialog-centered',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        performanceList: lstPerformanceResults,
        totalBranchCapital,
        totalBranchCount,
        startDate,
        endDate,
      },
    });
  }

  openBranchStatsDialog(lstStats: any[]) {
    return this.modalService.open(BranchStatsReportComponent, {
      modalClass: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        statsList: lstStats,
        isReadOnly: true,
      },
    });
  }

  openBranchReportDialog(
    lstBranchReports: any[],
    areaName: string = '',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(BranchReportComponent, {
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        branchReportList: lstBranchReports,
        areaName,
        startDate,
        endDate,
      },
    });
  }

  openBranchReportByAreaDialog(
    lstBranchReportsByArea: any[],
    areaName: string = '',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(BranchReportByAreaComponent, {
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        branchReportByAreaList: lstBranchReportsByArea,
        areaName,
        startDate,
        endDate,
      },
    });
  }

  openMaxLoansDialog(clientType: any, wageFrequency: any) {
    return this.modalService.open(MaximumLoansDialogComponent, {
     modalClass: 'modal-md modal-dialog-centered',
      data: {
        clientType: clientType,
        wageFrequency: wageFrequency,
      },
    });
  }

  openClientMismatchDialog(
    quoteId: number,
    lmsClientName: string,
    lmsClientSurname: string,
    bureauClientName: string,
    bureauClientSurname: string,
    errTitle: string,
  ) {
    return this.modalService.open(ClientMismatchDialogComponent, {
      modalClass: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        lmsClientName: lmsClientName,
        lmsClientSurname: lmsClientSurname,
        bureauClientName: bureauClientName,
        bureauClientSurname: bureauClientSurname,
        quoteId: quoteId,
      },
    });
  }

  openClientMismatchDialog1(rejectData: any) {
    console.log('asffqefe');

    return this.modalService.open(ClientMismatchDialogComponent, {
      modalClass: 'modal-md modal-dialog-centered',
      data: {
        data: rejectData,
        lmsClientName: rejectData.lmsClientName,
      },
    });
  }

  openLmsDataReportDialog(
    reportData: any[],
    reportTitle: string = 'LMS Data Report',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(LmsDataReportComponent, {
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        lmsDataReportList: reportData,
        reportTitle: reportTitle,
        startDate: startDate,
        endDate: endDate },
    });
  }

  openCompletedBranchLoansDialog(
    lstCompletedBranchLoansReport: any[],
    reportTitle: string = 'Completed Branch Loans Report',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(CompletedLoansReportComponent, {
      modalClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        completedBranchLoanslst: lstCompletedBranchLoansReport,
        reportTitle,
        startDate,
        endDate,
      },
    });
  }


   openNupayReportDialog(
    lstNupayReport: any[],
    reportTitle: string = 'NuPay Report',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(NupayReportComponent, {
      modalClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        nupayReportList: lstNupayReport,
        reportTitle,
        startDate,
        endDate,
      },
    });
  }

  openScanningMonitoringDialog(
    lstScanningMonitoringReport: any[],
    reportTitle: string = 'Scanning Monitoring Report',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(ScanningMonitoringReportComponent, {
      modalClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        scanningMonitoringReportList: lstScanningMonitoringReport,
        reportTitle,
        startDate,
        endDate,
      },
    });
  }

openFieldLoansReportDialog(
    lstFieldLoansReport: any[],
    reportTitle: string = 'Field Loans Report',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(FieldLoansReportComponent, {
      modalClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: true,
      data: {
        fieldLoansReportList: lstFieldLoansReport,
        reportTitle,
        startDate,
        endDate,
      },
    });
  }

  openLexisNexisReportDialog(
    lexisNexisReportList: any[],
    reportTitle: string = 'Lexis Nexis Report',
    startDate: string = '',
    endDate: string = '',
  ) {
    return this.modalService.open(LexisNexisReportComponent, {
      modalClass: 'modal-xl modal-top modal-dialog-scrollable',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      data: { lmsDataReportLst: lexisNexisReportList, reportTitle, startDate, endDate },
    });
  }

  openLoanChecklistDialog(isFirstLoan: boolean, isFirstLoanOfTheDay: boolean) {
    return this.modalService.open(LoanChecklistDialogComponent, {
      modalClass: 'modal-lg modal-dialog-centered',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      data: {
        isFirstLoan: isFirstLoan,
        isFirstLoanOfTheDay: isFirstLoanOfTheDay
      },
    });
  }
}

/**
 * Test-safe stub implementation of LMSDialogHandlerMockService.
 *
 * Purpose:
 * - Break circular imports during test bootstrap by avoiding application component imports.
 * - Provide no-op implementations for the methods used throughout the application so
 *   TypeScript compilation succeeds and tests can inject the service safely.
 *
 * Note: This is intentionally minimal and should be replaced by the real implementation
 * for production builds or during feature development. For long-term correctness,
 * resolve the original circular import and restore the full service implementation.
 */
@Injectable({ providedIn: 'root' })
export class LMSDialogHandlerMockService {
  public AuditmodalRef: MdbModalRef<any> | null = null;

  constructor(private modalService: MdbModalService) {}

  // Common dialog openings used by components. All return safe defaults.
  openClientDocuments(
    _clientModel: any,
    _quoteId?: number | string,
    _readonly = false,
  ): void {
    return;
  }

  openEmploymentDialog(
    _employment: any,
    _quoteModel: any,
    _addresses: any,
    _readonly = false,
  ): any {
    return null;
  }

  openUpdateEmployerDialog(_employerModel: any): any {
    return null;
  }

  openAddressDialog(_quoteModel: any, _addresses: any, _readonly = false): any {
    return null;
  }

  openNextofKinDialog(
    _nextOfKin: any,
    _quoteModel: any,
    _addresses: any,
    _readonly = false,
  ): any {
    return null;
  }

  openBankingDetailsDialog(
    _bankingDetails: any,
    _clientDetails: any,
    _readonly = false,
  ): any {
    return null;
  }

  openDocScanning(
    _quoteModel: any,
    _quoteId?: number | string,
    _readonly = false,
    _fromLoanDetails = false,
  ): any {
    return null;
  }

  openDocumentViewer(_fileSrc: any, _pfileName: string): any {
    return null;
  }

  openCompuscanOffersDialog(
    _compuscanOffers: any,
    _withOverride: boolean = false,
  ): any {
    return null;
  }

  openLexisNexisDialog(_header: any, _message: any, _title: string): void {
    return;
  }

  openBiometricsDialog(
    _biometricsInfo: any,
    _biometricType: string,
    _userBiometricsData?: any,
  ): any {
    return null;
  }

  openBMOTPOverrideDialog(
    _preauthBiometricsData: any,
    _biometricType: string,
    _userBiometricsData?: any,
  ): any {
    return null;
  }

  openLMSDialog(
    _confirmMsg: string,
    _msgtitle: string,
    _withTimer: boolean = false,
    _isError = false,
  ): any {
    return null;
  }

  openMdbComplianceNotification(): any {
    return null;
  }

  openSigninDialog(): any {
    return null;
  }

  openRiskMatrixScoresDialog(_riskAnalysis: any): void {
    return;
  }

  openRejectOfferDialog(
    _currQuote: any,
    _setToCancelOrReject: any = 'Rejection',
  ): any {
    return null;
  }

  openPayslipDialog(
    _month: string | number,
    _payslipModel?: any | null,
    _clientType?: string,
  ): any {
    return null;
  }

  openAuditTrailDialogByQuoteId(_quoteId: number): any {
    return null;
  }

  openAuditTrailDialogbyIdNumber(_idNumber: string): any {
    return null;
  }

  openOfferAuditDialog(_lstaudit: any): void {
    return;
  }

  openCBSGuideDialog(): void {
    return;
  }

  openQuoteNotesDialog(
    _quoteID: number,
    _quotenotes: any,
    _readonly = false,
  ): any {
    return null;
  }

  openBankAccountListDialog(
    _bankingDetails: any,
    _clientDetails: any,
    _readonly = false,
  ): any {
    return null;
  }

  // Additional commonly-used report/dialog methods (safe defaults)
  openBranchPerformanceDialog(_lstPerformanceResults: any[]): any {
    return null;
  }

  openBranchStatsDialog(_lstStats: any[]): any {
    return null;
  }

  openMaxLoansDialog(_clientType: any, _wageFrequency: any): any {
    return null;
  }

  // Methods with slightly different naming (typos) that are referenced in the
  // codebase. Provide no-op implementations to satisfy TypeScript.
  openOverrideReportDailog(_data: any): any {
    return null;
  }

  openClientChangeLogReportDailog(_data: any): any {
    return null;
  }

  openUserChangeLogReportDailog(_data: any): any {
    return null;
  }

  openBranchLoansDailog(_data: any): any {
    return null;
  }

  openLoanChecklistDialog() {
    return null
  }
}



